import { ColumnDef } from "@tanstack/react-table";
import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  AuditLog,
  EXPORT_TYPES,
  REPORT_NAMES,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
} from "../../../constants/common";
import {
  downloadFile,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import { ExportAuditLogsReportForExcelPDFBody } from "../../../hooks/report/interface";
import {
  useExportAuditLogsReportForExcel,
  useExportAuditLogsReportForPDF,
} from "../../../hooks/report/mutation";
import StatusCodes from "../../../constants/StatusCodes";

interface AuditLogsReportTableProps {
  allLogs: AuditLog[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  exportBody?: ExportAuditLogsReportForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const AuditLogsReportTable = ({
  allLogs,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  exportBody,
  pageSize,
  setPageSize,
}: AuditLogsReportTableProps) => {
  const { t, i18n } = useTranslation();

  const { mutateAsync: ExportAuditLogsReportForExcel } =
    useExportAuditLogsReportForExcel();
  const { mutateAsync: ExportAuditLogsReportForPDF } =
    useExportAuditLogsReportForPDF();

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const columns: ColumnDef<AuditLog>[] = useMemo(
    () => [
      {
        accessorKey: "fullName",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("fullName")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.fullName}</span>;
        },
      },
      {
        accessorKey: "s_Actions",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("action")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_Actions}</span>;
        },
      },
      {
        accessorKey: "dT_AuditDatetime",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("logTime")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {formattedDateTimeWithoutSec(row.original.dT_AuditDatetime)}
            </span>
          );
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      // {
      //   accessorKey: "s_BuildingName_En",
      //   header: ({ column }) => (
      //     <Button
      //       className="flex w-full justify-between text-md"
      //       variant="ghost"
      //       onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
      //     >
      //       {t("building")}
      //       <ArrowUpDown className="ml-2 h-4 w-4" />
      //     </Button>
      //   ),
      //   cell: ({ row }) => {
      //     return (
      //       <span>
      //         {i18n.language === "en"
      //           ? row.original.s_BuildingName_En
      //           : row.original.s_BuildingName_Ar}
      //       </span>
      //     );
      //   },
      // },
      {
        accessorKey: "logType",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("logType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.logType}</span>;
        },
      },
    ],
    [i18n.language, t]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (type === EXPORT_TYPES.EXCEL) {
          res = await ExportAuditLogsReportForExcel(body);
        } else {
          res = await ExportAuditLogsReportForPDF(body);
        }
        downloadFile(res, REPORT_NAMES.AUDIT_LOGS_REPORT);
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportAuditLogsReportForExcel,
      ExportAuditLogsReportForPDF,
      exportBody,
      i18n.language,
      t,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allLogs}
        columns={columns}
        search={search}
        setSearch={setSearch}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isExportExcel
        onExport={onExport}
        isSearch
        tableId={TABLE_IDS.AUDIT_LOGS_REPORT}
        setVisibleColumnKeys={setVisibleColumnKeys}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  );
};

export default memo(AuditLogsReportTable);
